import { z } from 'zod';

/**
 * Mirrors `backend/app/api/errors.py`'s `ErrorResponse`: every failure the API chooses to
 * return has a stable `code` the frontend can branch on and a `message` that is already
 * Spanish and already safe to show the owner.
 */
export const errorResponseSchema = z.object({
  code: z.string(),
  message: z.string(),
});

export type ErrorResponseBody = z.infer<typeof errorResponseSchema>;

/** Thrown by every `api/http.ts` request. Carries only what the UI is allowed to show. */
export class ApiError extends Error {
  /** HTTP status, or `0` for a request that never reached the server. */
  readonly status: number;
  /** Stable machine code (`invalid_credentials`, `not_authenticated`, `csrf_failed`, ...). */
  readonly code: string;
  /**
   * The `x-correlation-id` the backend answered with, or `undefined` for a request that never
   * reached it (`api/http.ts`'s `NETWORK_ERROR_CODE` path). The same header name and format
   * `app/correlation.py` and `notevision_agent/correlation.py` already share, so this value is
   * exactly what to search for in the backend's or the worker's logs to trace this one failed
   * request - never shown to the owner, only surfaced in the development-only error detail
   * (`shell/ErrorScreen.tsx`'s `detail` prop).
   */
  readonly correlationId: string | undefined;

  constructor(status: number, code: string, message: string, correlationId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }

  /** `true` for the uniform "no valid session" refusal every owner route shares. */
  get isSessionInvalid(): boolean {
    return this.status === 401;
  }
}

export const NETWORK_ERROR_CODE = 'network_error';
export const UNEXPECTED_RESPONSE_CODE = 'unexpected_response';

export const NETWORK_ERROR_MESSAGE =
  'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
export const UNEXPECTED_RESPONSE_MESSAGE = 'La respuesta del servidor no era la esperada.';
